import { Box, Textarea } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Field from "src/components/field";
import FixedCostForm from "src/components/ProjectForms/FixedCostForm";
import RetainerForm from "src/components/ProjectForms/RetainerForm";
import HourlyRateForm from "src/components/ProjectForms/HourlyRateForm";
import CharacterCounter from "src/components/CharacterCount/CharacterCounter";
import { JobDetail, Talent } from "src/lib/endpoints/hire/hire.type";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";
import { useGiveOfferMutation } from "src/lib/endpoints/hire/hire.api";
import { useNavigate, useParams } from "react-router-dom";
import useAlert from "src/hooks/use-alert";
import { OfferForm } from "../ProjectSetup/types/OfferObject";
import PageShell from "src/components/PageShell";
import BackLink from "src/components/BackLink";
import { Card, Eyebrow } from "src/components/Card";
import { SectionHeader, cn } from "src/components/ui-x";
import EntityAvatar from "src/components/EntityAvatar";
import { commaSeperatedNumberFormat } from "src/lib/utils/format-text";

const workType = [
  { id: "fixedcost", label: "Fixed cost", helperText: "Paid by milestone" },
  { id: "retainer", label: "Retainer", helperText: "Paid on a schedule" },
  {
    id: "hourly",
    label: "Hourly rate",
    helperText: "Coming soon",
    enable: false,
  },
];

export const BasicGiveOffer = ({
  jobDetail,
  isBasic,
  talentName,
  talent,
}: {
  jobDetail: JobDetail;
  isBasic?: boolean;
  /** The talent's full name, for the page header. */
  talentName?: string;
  /** The matched talent, for the identity card. */
  talent?: Talent;
}) => {
  const { user } = useTypedSelector(selectUserState);
  const { jobId, userId } = useParams();
  const navigate = useNavigate();
  const { giveOffer, isLoading } = useGiveOfferMutation({
    jobId: jobId ?? "",
    userId: userId ?? "",
  });
  const { state: alertState, open: openAlert, Alert } = useAlert();

  const currency = {
    code: user?.userCompany?.currency ?? "USD",
    symbol: user?.userCompany?.currency_symbol ?? "$",
  };

  const [description, setDescription] = useState<string | undefined>(
    jobDetail?.jobDetails ?? undefined
  );
  const [radioInputSelected, setRadioInputSelected] = useState(
    jobDetail?.payment_type
  );

  useEffect(() => {
    setDescription(jobDetail?.jobDetails ?? undefined);
    setRadioInputSelected(jobDetail?.payment_type);
  }, [jobDetail]);

  /* Surname cut to an initial, as everywhere else a client sees a talent. */
  const displayName = [
    talent?.firstName?.trim(),
    talent?.lastName?.trim() ? `${talent.lastName.trim()[0]}.` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const budget = jobDetail?.project_budget || jobDetail?.salary_range;
  const jobFacts = [
    {
      label: "Budget",
      value: budget ? `${currency.symbol}${commaSeperatedNumberFormat(budget)}` : "",
    },
    { label: "Work type", value: jobDetail?.workType },
    { label: "Level", value: jobDetail?.level },
    { label: "Role", value: jobDetail?.stack?.replace(/-/g, " ") },
  ];

  const handleSubmitOffer = (data: OfferForm) => {
    const formData = new FormData();
    formData.append("description", data.description);
    formData.append("work_type", data.work_type);
    formData.append("payment_type", data.payment_type);
    formData.append("agreed_total_cost", data.agreed_total_cost);
    formData.append("startDate", data.startDate);
    formData.append("milestones", JSON.stringify(data.milestones));
    formData.append("retainer_agreed_interval", data.retainer_agreed_interval ?? "");
    formData.append("agreed_duration", data.agreed_duration);
    if (data.retainer_agreed_interval_payment) {
      formData.append(
        "retainer_agreed_interval_payment",
        data.retainer_agreed_interval_payment
      );
    }

    giveOffer(formData, {
      onSuccess: async (res) => {
        if (res?.status) {
          navigate("/project");
        } else {
          openAlert({
            ...alertState,
            title: res?.message ?? "Failed to send offer",
            variant: "error",
            active: true,
          });
        }
      },
      onError: (error: any) => {
        openAlert({
          ...alertState,
          title: error?.response?.data?.message ?? error?.message ?? "",
          variant: "error",
          active: true,
        });
      },
    });
  };

  return (
    <PageShell width="detail">
      <span className="fixed right-3 top-3 z-30">
        <Alert />
      </span>

      <BackLink
        to={`/hire-requests/${jobId ?? ""}/${userId ?? ""}`}
        label="Talent profile"
      />

      {/* The job is the heading; "Give offer" says what this page does. */}
      <SectionHeader
        eyebrow="Give offer"
        title={jobDetail?.jobTitle || "New offer"}
      />

      {/* Who the offer is for and what was asked for, laid out like the
          identity card on KYB Details, so the terms below are set with the
          brief in view rather than from memory. */}
      <Card>
        <div className="flex items-center gap-4">
          <EntityAvatar
            src={talent?.photo}
            name={talentName}
            className="h-14 w-14"
          />
          <div className="min-w-0">
            <h3 className="truncate font-display text-lg font-bold text-brand-navy">
              {displayName || "Selected talent"}
            </h3>
            <p className="truncate text-sm capitalize text-muted-foreground">
              {[talent?.primaryStack, talent?.continent]
                .filter(Boolean)
                .join(" · ")
                .replace(/-/g, " ")}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-4 md:grid-cols-4">
          {jobFacts.map(({ label, value }) => (
            <div key={label} className="rounded-lg bg-surface px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </p>
              <p className="mt-0.5 text-sm font-medium capitalize text-brand-navy">
                {value || "—"}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <Eyebrow>Scope</Eyebrow>
        <h3 className="mb-4 text-base font-bold text-brand-navy">
          Project details
        </h3>
        <Field
          helperText={
            <Box textAlign="right">
              <CharacterCounter text={description} limit={2000} />
            </Box>
          }
        >
          <Textarea
            name="description"
            id="description"
            aria-label="Project details"
            placeholder="Enter project description here"
            rows={5}
            p={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            resize="none"
            borderWidth={"thin"}
            rounded="lg"
          />
        </Field>
      </Card>

      <Card>
        <Eyebrow>Terms</Eyebrow>
        <h3 className="mb-4 text-base font-bold text-brand-navy">
          Work type
        </h3>

        <div
          role="radiogroup"
          aria-label="Work type"
          className="grid gap-3 sm:grid-cols-3"
        >
          {workType.map((item) => {
            const selected = radioInputSelected === item.id;
            const disabled = item.enable === false;
            return (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => setRadioInputSelected(item.id)}
                className={cn(
                  "flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                  selected
                    ? "border-brand-orange bg-brand-orange-100/40"
                    : "hover:bg-surface",
                  disabled && "cursor-not-allowed opacity-50 hover:bg-transparent"
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border",
                    selected ? "border-brand-orange" : "border-border"
                  )}
                >
                  {selected && (
                    <span className="h-2 w-2 rounded-full bg-brand-orange" />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-brand-navy">
                    {item.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {item.helperText}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* The chosen type's form sits in the same card as the choice. It used
            to be headed by a second, larger copy of the option's name. */}
        {radioInputSelected && (
          <div className="mt-6 border-t pt-6">
            {radioInputSelected === "hourly" && (
              <HourlyRateForm
                description={description ?? ""}
                jobDetail={jobDetail}
              />
            )}

            {radioInputSelected === "fixedcost" && (
              <FixedCostForm
                currencySymbol={currency.symbol}
                description={description ?? ""}
                jobDetail={jobDetail}
                isBasic={isBasic}
                onDone={handleSubmitOffer}
              />
            )}

            {radioInputSelected === "retainer" && (
              <RetainerForm
                currencySymbol={currency.symbol}
                description={description ?? ""}
                jobDetail={jobDetail}
                isBasic={isBasic}
                onDone={handleSubmitOffer}
              />
            )}
          </div>
        )}
      </Card>
    </PageShell>
  );
};
